import type { UniqueIdentifier } from '@dnd-kit/abstract';

export type MovementPoint = { x: number; y: number };
export type MovementDirection = 'up' | 'down' | 'left' | 'right';
export type MovementAxis = 'x' | 'y' | 'both';
export type DragAxis = 'x' | 'y' | 'dynamic';

export const MOVEMENT_INTERVAL_SENSITIVITY_PX = 10;

export const getDirection = (
  dragAxis: DragAxis,
  delta: MovementPoint,
): MovementDirection | null => {
  if (dragAxis === 'dynamic') {
    if (Math.abs(delta.y) > Math.abs(delta.x)) {
      return delta.y === 0 ? null : delta.y > 0 ? 'down' : 'up';
    }

    return delta.x === 0 ? null : delta.x > 0 ? 'right' : 'left';
  }
  if (dragAxis === 'x') {
    return delta.x === 0 ? null : delta.x > 0 ? 'right' : 'left';
  }

  return delta.y === 0 ? null : delta.y > 0 ? 'down' : 'up';
};

export const getMovementDirectionFromDelta = ({
  deltaX,
  deltaY,
  axis = 'both',
}: {
  deltaX: number;
  deltaY: number;
  axis?: MovementAxis;
}): MovementDirection | null => {
  return getDirection(axis === 'both' ? 'dynamic' : axis, { x: deltaX, y: deltaY });
};

export const resolveMovementInterval = ({
  point,
  previousAnchor,
  previousDirection,
  sensitivityPx = MOVEMENT_INTERVAL_SENSITIVITY_PX,
  axis = 'both',
}: {
  point: MovementPoint | null;
  previousAnchor: MovementPoint | null;
  previousDirection: MovementDirection | null;
  sensitivityPx?: number;
  axis?: MovementAxis;
}): { direction: MovementDirection | null; anchor: MovementPoint | null } => {
  if (!point) {
    return { direction: previousDirection, anchor: previousAnchor };
  }

  if (!previousAnchor) {
    return { direction: previousDirection, anchor: point };
  }

  const deltaX = point.x - previousAnchor.x;
  const deltaY = point.y - previousAnchor.y;
  const nextDirection =
    getMovementDirectionFromDelta({ deltaX, deltaY, axis }) ?? previousDirection;
  const crossedSensitivity = Math.abs(deltaX) > sensitivityPx || Math.abs(deltaY) > sensitivityPx;

  return {
    direction: nextDirection,
    anchor: crossedSensitivity ? point : previousAnchor,
  };
};

export const createMovementIntervalTracker = ({
  sensitivityPx = MOVEMENT_INTERVAL_SENSITIVITY_PX,
  axis = 'both',
}: {
  sensitivityPx?: number;
  axis?: MovementAxis;
} = {}) => {
  const anchorBySource = new Map<UniqueIdentifier, MovementPoint>();
  const directionBySource = new Map<UniqueIdentifier, MovementDirection | null>();

  return {
    track(sourceId: UniqueIdentifier, point: MovementPoint): MovementDirection | null {
      const interval = resolveMovementInterval({
        point,
        previousAnchor: anchorBySource.get(sourceId) ?? null,
        previousDirection: directionBySource.get(sourceId) ?? null,
        sensitivityPx,
        axis,
      });
      if (interval.anchor) {
        anchorBySource.set(sourceId, interval.anchor);
      }
      directionBySource.set(sourceId, interval.direction);
      return interval.direction;
    },
    reset(sourceId?: UniqueIdentifier): void {
      if (sourceId !== undefined) {
        anchorBySource.delete(sourceId);
        directionBySource.delete(sourceId);
        return;
      }

      anchorBySource.clear();
      directionBySource.clear();
    },
  };
};
